import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";

const SURVEY_URL = "https://haevn-mvp.vercel.app";

// HAEVN Logo SVG
export const HaevnLogo = ({ className = "", color = "#1E2A4A" }) => (
  <svg viewBox="0 0 1554.72 337.36" className={className} fill={color}>
    <g>
      <g>
        <path d="M48.51,97.71h109.87v44.85h19.21v-44.85h108.14c17.11,0,31.03,13.92,31.03,31.03s-13.92,31.03-31.03,31.03h-13.12v19.21h13.12c27.71,0,50.24-22.54,50.24-50.24s-22.54-50.24-50.24-50.24H48.51c-16.15,0-29.3-13.14-29.3-29.29v-.69c0-16.15,13.14-29.3,29.3-29.3s29.29,13.14,29.29,29.3v16.24h19.21v-16.24C97.01,21.76,75.25,0,48.51,0S0,21.76,0,48.51v.69c0,26.75,21.76,48.51,48.51,48.51Z"/>
        <path d="M287.46,239.66h-109.88v-46.23h-19.21v46.23H50.24c-17.11,0-31.03-13.92-31.03-31.03s13.92-31.03,31.03-31.03h13.12v-19.21h-13.12c-27.71,0-50.24,22.54-50.24,50.24s22.54,50.24,50.24,50.24h237.22c16.15,0,29.3,13.14,29.3,29.3v.69c0,16.15-13.14,29.3-29.3,29.3s-29.29-13.14-29.29-29.3v-16.24h-19.21v16.24c0,26.75,21.76,48.51,48.5,48.51s48.51-21.76,48.51-48.51v-.69c0-26.75-21.76-48.51-48.51-48.51Z"/>
        <path d="M177.59,49.2c0-16.54,13.45-29.99,29.99-29.99s29.99,13.45,29.99,29.99v15.55h19.21v-15.55c0-27.13-22.07-49.2-49.2-49.2s-49.2,22.07-49.2,49.2v15.55h19.21v-15.55Z"/>
        <path d="M158.38,288.16c0,16.54-13.45,29.99-29.99,29.99s-29.99-13.45-29.99-29.99v-15.55h-19.21v15.55c0,27.13,22.07,49.2,49.2,49.2s49.2-22.07,49.2-49.2v-15.55h-19.21v15.55Z"/>
        <polygon points="256.78 227.46 256.78 111.3 237.57 111.3 237.57 158.38 98.4 158.39 98.4 111.29 79.19 111.29 79.19 227.45 98.4 227.45 98.4 177.6 237.57 177.59 237.57 227.46 256.78 227.46"/>
      </g>
      <g>
        <polygon points="597.4 151.71 477.49 151.71 477.49 53.9 445.95 53.9 445.95 283.47 477.49 283.47 477.49 182.45 597.4 182.45 597.4 283.47 629.1 283.47 629.1 53.9 597.4 53.9 597.4 151.71"/>
        <polygon points="1090.09 84.63 1078.12 53.9 922.51 53.9 922.51 283.47 1090.09 283.47 1090.09 252.57 954.04 252.57 954.04 179.25 1050.1 179.25 1050.1 148.67 954.04 148.67 954.04 84.47 1090.09 84.63"/>
        <polygon points="1221.82 239.28 1150.1 53.9 1117.44 53.9 1206.94 283.47 1236.55 283.47 1326.05 53.9 1293.23 53.9 1221.82 239.28"/>
        <polygon points="1523.34 53.9 1523.34 226.95 1390.3 53.9 1362.77 53.9 1362.77 283.47 1394.15 283.47 1394.15 110.41 1527.18 283.47 1554.72 283.47 1554.72 53.9 1523.34 53.9"/>
        <polygon points="761.87 53.9 665.18 283.47 699.12 283.47 775.96 98.72 852.33 283.47 886.43 283.47 789.73 53.9 761.87 53.9"/>
      </g>
    </g>
  </svg>
);

const navLinks = [
  { to: "/", label: "Home" },
  { to: "/cities", label: "Active Cities" },
  { to: "/pricing", label: "Membership Plans" },
  { to: "/about", label: "About" },
];

export default function Navigation() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-100" data-testid="global-nav">
      <div className="max-w-6xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo">
          <HaevnLogo className="h-7 w-auto" />
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`text-sm font-subhead transition-colors duration-200 ${
                location.pathname === link.to
                  ? "text-haevn-navy"
                  : "text-gray-400 hover:text-haevn-navy"
              }`}
              data-testid={`nav-${link.label.toLowerCase().replace(/\s/g, "-")}`}
            >
              {link.label}
            </Link>
          ))}
          <a
            href={SURVEY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 bg-haevn-gold text-white px-5 py-2 font-subhead text-sm hover:bg-haevn-gold/90 transition-all duration-200"
            data-testid="nav-cta"
          >
            Start Your Survey
            <ArrowRight className="w-4 h-4" />
          </a>
        </nav>

        {/* Mobile Toggle */}
        <button
          className="md:hidden text-haevn-navy"
          onClick={() => setMobileOpen(!mobileOpen)}
          data-testid="mobile-menu-toggle"
        >
          {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Nav */}
      {mobileOpen && (
        <nav className="md:hidden bg-white border-t border-gray-100 px-6 py-6 space-y-4" data-testid="mobile-nav">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              onClick={() => setMobileOpen(false)}
              className={`block text-base font-subhead ${
                location.pathname === link.to
                  ? "text-haevn-navy"
                  : "text-gray-400"
              }`}
            >
              {link.label}
            </Link>
          ))}
          <a
            href={SURVEY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 bg-haevn-gold text-white px-5 py-3 font-subhead text-sm w-full justify-center mt-2"
          >
            Start Your Survey
            <ArrowRight className="w-4 h-4" />
          </a>
        </nav>
      )}
    </header>
  );
}
